import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import Panel from "../../../components/ui/Panel/Panel.jsx";
import Button from "../../../components/ui/Button/Button.jsx";
import Banner from "../../../components/ui/Banner/Banner.jsx";
import FormField from "../../../components/forms/FormField/FormField.jsx";
import TextInput from "../../../components/forms/TextInput/TextInput.jsx";
import Checkbox from "../../../components/forms/Checkbox/Checkbox.jsx";
import { useApp } from "../../../hooks/useApp.js";
import { money } from "../../../utils/format.js";
import { trackPurchase } from "../../../seo/track";
import { getToken } from "../../../services/api.js";
import {
  createServiceFeeIntent,
  getServiceFeeConfig,
  getServiceFeeStatus,
  createServiceFeeTopUpIntent,
  getServiceFeeTopUpConfig,
  getServiceFeeTopUpStatus,
} from "../../../services/paymentDetailsService.js";
import {
  getSubscriptionEligibility,
  reserveSubscriptionCredit,
  getSubscriptionUsageStatus,
  getSubscriptionUpsell,
  dismissSubscriptionUpsell,
  getSubscriptionTopUpEligibility,
  reserveSubscriptionTopUpCredit,
  getSubscriptionTopUpUsageStatus,
} from "../../../services/subscriptionService.js";
import { submitReturn } from "../../../services/efileService.js";

/* Same visual as other steps' ErrorMsg — red text under the field */
function ErrorMsg({ msg }) {
  if (!msg) return null;
  return <p className="text-[11px] text-[var(--danger)] mt-[4px]">{msg}</p>;
}

// Stripe only needs card details from the Payment Element itself. We collect
// ZIP with our own field (required for AVS fraud checks) and pass it — along
// with a fixed billing country — via payment_method_data at confirm time. No
// street address / city / state are collected or sent to Stripe.
const paymentElementOptions = {
  fields: { billingDetails: { address: "never" } },
};

const BILLING_COUNTRY = "US";


// Maps a Stripe.js error to plain, user-facing copy (no decline codes/jargon).
function friendlyStripeError(error) {
  if (!error) return "We couldn't process your payment. Please try again.";
  if (error.type === "card_error" || error.type === "validation_error") {
    return error.message || "Your card could not be processed. Please check the details and try again.";
  }
  return "We couldn't process your payment. Please try again.";
}

// "I agree to..." wording for the fee checkbox, shared by FeeForm and FeeFormPreview. A
// referral credit is named in the sentence (not silently netted) so the filer agrees to the
// same breakdown the box above it shows.
function consentText({ amount, serviceFeeAmount, referralCreditAmount, processingFeeAmount }) {
  const credit = Number(referralCreditAmount) > 0 ? ` less a ${money(referralCreditAmount)} referral credit` : "";
  if (Number(processingFeeAmount) > 0) {
    return `I agree to the service fee of ${money(serviceFeeAmount)}${credit}, plus a ${money(
      processingFeeAmount
    )} processing fee (total ${money(amount / 100)})`;
  }
  return credit
    ? `I agree to the service fee of ${money(serviceFeeAmount)}${credit} (total ${money(amount / 100)})`
    : `I agree to the service fee of ${money(amount / 100)}`;
}

// TEMPORARY preview form — shown when Stripe isn't configured yet (blank
// stripe.publishable-key in the backend's application.properties, see
// PaymentDetailsStep's configError branch below), so the page's layout
// (name fields, ZIP, consent) can still be reviewed without a live Stripe
// account. The card box is a static placeholder (no useStripe/useElements,
// so it doesn't need an <Elements> wrapper) and Submit is disabled. Delete
// this component and the configError preview branch once real Stripe test
// keys are set — FeeForm below is the real, fully-wired form.
function FeeFormPreview({ onBack, amount, serviceFeeAmount, referralCreditAmount = "0.00", processingFeeAmount }) {
  const [form, setForm] = useState({ firstName: "", lastName: "", zip: "" });
  const [consent, setConsent] = useState(false);

  const handleNameChange = (key) => (e) => {
    const val = e.target.value
      .replace(/^ /, "")
      .replace(/[^A-Za-z\-' ]/g, "")
      .replace(/ {2,}/g, " ")
      .slice(0, 20);
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const handleZipChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 12);
    const val = raw.length <= 5 ? raw : `${raw.slice(0, 5)}-${raw.slice(5)}`;
    setForm((prev) => ({ ...prev, zip: val }));
  };

  return (
    <Panel padded>
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
        Payment details
      </div>
      <h2 className="text-2xl m-[4px_0]">Pay the e-filing service fee</h2>
      <p className="text-[var(--muted)] max-w-[62ch] mb-4">
        A one-time service fee for using EasyFile2290's e-filing service — separate from the tax you owe the IRS.
      </p>

      <Banner tone="warn" icon="⚠" className="mb-4">
        Preview only — Stripe test keys aren't configured yet, so this form can't take a real payment.
      </Banner>

      <div className="grid gap-3.5 sm:grid-cols-2">
        <FormField label="First name" required>
          <TextInput placeholder="John" value={form.firstName} onChange={handleNameChange("firstName")} maxLength={20} />
        </FormField>
        <FormField label="Last name" required>
          <TextInput placeholder="Doe" value={form.lastName} onChange={handleNameChange("lastName")} maxLength={20} />
        </FormField>
      </div>

      <FormField label="Card details" required>
        <div className="border border-[var(--line-2)] rounded-[var(--r-sm)] p-3 bg-[var(--panel-2)] text-sm text-[var(--muted)]">
          Card number, expiry, and CVC render here once Stripe test keys are configured.
        </div>
      </FormField>

      <p className="text-[12px] text-[var(--muted)] mt-2 mb-4">
        We keep only the card type and last four digits, so you can recognise the payment on your
        receipt and so we can help you if a question or refund comes up later.
      </p>

      <FormField label="ZIP" required style={{ maxWidth: 220 }}>
        <TextInput mono placeholder="10001 or 10001-1234" value={form.zip} onChange={handleZipChange} maxLength={13} />
      </FormField>

      <div className="border border-[var(--line-2)] rounded-[var(--r-sm)] p-3 bg-[var(--panel-2)] mt-4">
        <div className="flex justify-between text-[13px] text-[var(--muted)] mb-1">
          <span>Service fee</span>
          <span>{money(serviceFeeAmount)}</span>
        </div>
        {Number(referralCreditAmount) > 0 && (
          <div className="flex justify-between text-[13px] text-[var(--ok)] mb-1">
            <span>Referral credit</span>
            <span>−{money(referralCreditAmount)}</span>
          </div>
        )}
        {Number(processingFeeAmount) > 0 && (
          <div className="flex justify-between text-[13px] text-[var(--muted)] mb-1">
            <span>Processing fee</span>
            <span>{money(processingFeeAmount)}</span>
          </div>
        )}
        <div className="flex justify-between text-[14px] font-semibold text-[var(--ink)] pt-2 mt-1 border-t border-[var(--line-2)]">
          <span>Total</span>
          <span>{money(amount / 100)}</span>
        </div>
      </div>

      <Checkbox
        id="serviceFeeConsentPreview"
        title={
          consentText({ amount, serviceFeeAmount, referralCreditAmount, processingFeeAmount })
        }
        checked={consent}
        onChange={(e) => setConsent(e.target.checked)}
      />

      <div className="flex justify-between gap-3 mt-6">
        <Button variant="ghost" onClick={onBack}>
          ← Back
        </Button>
        <Button disabled>{`Pay ${money(amount / 100)} & submit →`}</Button>
      </div>
    </Panel>
  );
}

// Inner form — lives inside <Elements> so it can use the Stripe hooks.
// createIntentFn/statusFn/heading/description/serviceFeeSource let this same
// form serve the top-up flow too (an additional charge for vehicles added
// beyond what a rejected return already paid for, PaymentDetailsStep's
// alreadyPaid branch below) without duplicating the whole Stripe
// confirm/poll dance for a second, near-identical component.
// serviceFeeSource is "SERVICE_FEE_TOPUP" for a top-up: it's a SEPARATE
// payment (its own id, its own table - service_fee_topup_payment, not
// service_fee_payment) from the return's original service-fee payment, so
// state.serviceFee.paymentId/source are deliberately overwritten to point at
// THIS payment - Confirmation.jsx always shows the receipt for whichever
// payment was most recently made, which after a top-up is the top-up itself,
// not the return's original (now-stale) fee/credit receipt.
function FeeForm({
  onBack,
  returnId,
  amount,
  serviceFeeAmount,
  referralCreditAmount = "0.00",
  processingFeeAmount,
  showToast,
  dispatch,
  fileReturn,
  createIntentFn = createServiceFeeIntent,
  statusFn = getServiceFeeStatus,
  heading = "Pay the e-filing service fee",
  description = "A one-time service fee for using EasyFile2290's e-filing service — separate from the tax you owe the IRS.",
  serviceFeeSource = null,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(""); // '' | 'finalizing'
  // A failed payment is shown in a box above the Pay button that stays until the next try -
  // the corner toast alone disappears in under 3 seconds and filers were missing it.
  // { tone: "danger" | "warn", msg }
  const [payError, setPayError] = useState(null);
  const payErrorRef = useRef(null);

  useEffect(() => {
    if (payError && payErrorRef.current) {
      payErrorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [payError]);

  const failPayment = (msg, tone = "danger") => {
    setPayError({ tone, msg });
    showToast(msg);
  };
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({ firstName: "", lastName: "", zip: "" });

  const handleNameChange = (key) => (e) => {
    const val = e.target.value
      .replace(/^ /, "")
      .replace(/[^A-Za-z\-' ]/g, "")
      .replace(/ {2,}/g, " ")
      .replace(/-{2,}/g, "-")
      .replace(/'{2,}/g, "'")
      .slice(0, 20);
    setForm((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleZipChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 12);
    const val = raw.length <= 5 ? raw : `${raw.slice(0, 5)}-${raw.slice(5)}`;
    setForm((prev) => ({ ...prev, zip: val }));
    setErrors((prev) => ({ ...prev, zip: "" }));
  };

  const validate = () => {
    const errs = {};
    const first = form.firstName.trim();
    const last = form.lastName.trim();

    if (!first) errs.firstName = "First name is required.";
    else if (!/^[A-Za-z\-' ]+$/.test(first)) errs.firstName = "First name can only contain letters, hyphens, apostrophes, and spaces.";

    if (!last) errs.lastName = "Last name is required.";
    else if (!/^[A-Za-z\-' ]+$/.test(last)) errs.lastName = "Last name can only contain letters, hyphens, apostrophes, and spaces.";

    if (!/^\d{5}$|^\d{5}-\d{4}$|^\d{5}-\d{7}$/.test(form.zip)) {
      errs.zip = "Invalid ZIP code. Use 5, 9 (XXXXX-XXXX), or 12 (XXXXX-XXXXXXX) digits.";
    }

    if (!consent) errs.consent = "You must agree to the service fee.";

    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      showToast(Object.values(errs)[0]);
      return false;
    }
    return true;
  };

  // Poll our backend until the payment row is finalized (webhook/reconcile).
  // Returns 'SUCCESS' | 'FAILED' | 'PENDING' (timeout).
  const waitForFinal = async () => {
    for (let i = 0; i < 8; i++) {
      try {
        const p = await statusFn(returnId);
        if (p?.status === "SUCCESS") return "SUCCESS";
        if (p?.status === "FAILED") return "FAILED";
      } catch {
        /* keep polling */
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    return "PENDING";
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (!stripe || !elements) {
      showToast("Payment form is still loading. Please try again in a moment.");
      return;
    }
    setIsSubmitting(true);
    setPayError(null);
    try {
      // 1. Validate the Payment Element (card fields).
      const { error: submitError } = await elements.submit();
      if (submitError) {
        failPayment(friendlyStripeError(submitError));
        return;
      }

      // 2. Create the PaymentIntent server-side (name/zip only — no street
      //    address/city/state; Stripe never sees the address at all).
      let clientSecret;
      let paymentId;
      try {
        const created = await createIntentFn(returnId, form);
        clientSecret = created.clientSecret;
        paymentId = created.paymentId;
      } catch (err) {
        failPayment(err.message || "We couldn't start your payment. Please try again.");
        return;
      }

      // 3. Confirm. redirect:'if_required' keeps card 3DS in an in-page modal.
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        redirect: "if_required",
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              name: `${form.firstName} ${form.lastName}`.trim(),
              address: {
                line1: "",
                city: "",
                state: "",
                postal_code: form.zip,
                country: BILLING_COUNTRY,
              },
            },
          },
        },
      });

      if (error) {
        failPayment(friendlyStripeError(error));
        return;
      }

      // 4. Reflect Stripe's outcome; let our backend finalize the row.
      if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
        setStatus("finalizing");
        const final = await waitForFinal();
        if (final === "FAILED") {
          failPayment("Your payment couldn't be completed. Please try again.");
          return;
        }
        if (final === "PENDING") {
          // Stripe accepted the payment but our records haven't confirmed it
          // yet. Don't claim success or advance — the review/submit step
          // depends on a confirmed payment.
          failPayment("Your payment is still processing. We'll confirm it shortly — please try Continue again in a moment.", "warn");
          return;
        }
        // Revenue conversion - fired here, not on Stripe's `succeeded`, because
        // waitForFinal() has just confirmed our own row is SUCCESS. `amount` is
        // the total we collect in cents (service fee + processing fee), NOT the
        // HVUT being remitted to the IRS. See trackPurchase's note.
        trackPurchase({ transactionId: paymentIntent.id, valueCents: amount, returnId });
        // source is always set explicitly (never omitted) - SET_SERVICE_FEE merges the
        // patch into state.serviceFee, so omitting it would leave a stale source (e.g.
        // "SUBSCRIPTION_CREDIT" from an earlier return/step in this same browser session)
        // in place, pointing Confirmation.jsx's receipt lookup at the wrong payment.
        dispatch({
          type: "SET_SERVICE_FEE",
          patch: { status: "SUCCESS", paymentId, source: serviceFeeSource },
        });
        showToast("Payment successful!");
        setStatus("filing");
        await fileReturn();
      } else {
        failPayment("We couldn't confirm your payment. Please try again.");
      }
    } catch (err) {
      failPayment("We couldn't process your payment. Please try again.");
    } finally {
      setIsSubmitting(false);
      setStatus("");
    }
  };

  return (
    <Panel padded>
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
        Payment details
      </div>
      <h2 className="text-2xl m-[4px_0]">{heading}</h2>
      <p className="text-[var(--muted)] max-w-[62ch] mb-4">{description}</p>

      <div className="grid gap-3.5 sm:grid-cols-2">
        <FormField label="First name" required error={errors.firstName} showError={!!errors.firstName}>
          <TextInput
            placeholder="John"
            value={form.firstName}
            onChange={handleNameChange("firstName")}
            onBlur={() => setForm((p) => ({ ...p, firstName: p.firstName.replace(/\s+$/, "") }))}
            maxLength={20}
            invalid={!!errors.firstName}
            disabled={isSubmitting}
          />
        </FormField>
        <FormField label="Last name" required error={errors.lastName} showError={!!errors.lastName}>
          <TextInput
            placeholder="Doe"
            value={form.lastName}
            onChange={handleNameChange("lastName")}
            onBlur={() => setForm((p) => ({ ...p, lastName: p.lastName.replace(/\s+$/, "") }))}
            maxLength={20}
            invalid={!!errors.lastName}
            disabled={isSubmitting}
          />
        </FormField>
      </div>

      <FormField label="Card details" required>
        <div className="border border-[var(--line-2)] rounded-[var(--r-sm)] p-3 bg-[var(--panel)]">
          <PaymentElement options={paymentElementOptions} />
        </div>
      </FormField>

      <p className="text-[12px] text-[var(--muted)] mt-2 mb-4">
        We keep only the card type and last four digits, so you can recognise the payment on your
        receipt and so we can help you if a question or refund comes up later.
      </p>

      <FormField label="ZIP" required error={errors.zip} showError={!!errors.zip} style={{ maxWidth: 220 }}>
        <TextInput
          mono
          placeholder="10001 or 10001-1234"
          value={form.zip}
          onChange={handleZipChange}
          maxLength={13}
          invalid={!!errors.zip}
          disabled={isSubmitting}
        />
      </FormField>

      <div className="border border-[var(--line-2)] rounded-[var(--r-sm)] p-3 bg-[var(--panel-2)] mt-4">
        <div className="flex justify-between text-[13px] text-[var(--muted)] mb-1">
          <span>Service fee</span>
          <span>{money(serviceFeeAmount)}</span>
        </div>
        {Number(referralCreditAmount) > 0 && (
          <div className="flex justify-between text-[13px] text-[var(--ok)] mb-1">
            <span>Referral credit</span>
            <span>−{money(referralCreditAmount)}</span>
          </div>
        )}
        {Number(processingFeeAmount) > 0 && (
          <div className="flex justify-between text-[13px] text-[var(--muted)] mb-1">
            <span>Processing fee</span>
            <span>{money(processingFeeAmount)}</span>
          </div>
        )}
        <div className="flex justify-between text-[14px] font-semibold text-[var(--ink)] pt-2 mt-1 border-t border-[var(--line-2)]">
          <span>Total</span>
          <span>{money(amount / 100)}</span>
        </div>
      </div>

      <Checkbox
        id="serviceFeeConsent"
        title={
          consentText({ amount, serviceFeeAmount, referralCreditAmount, processingFeeAmount })
        }
        checked={consent}
        onChange={(e) => {
          setConsent(e.target.checked);
          setErrors((prev) => ({ ...prev, consent: "" }));
        }}
        disabled={isSubmitting}
      />
      <ErrorMsg msg={errors.consent} />

      {payError && (
        <div ref={payErrorRef} role="alert" className="mt-4">
          <Banner tone={payError.tone} icon="⚠">
            <b>{payError.tone === "warn" ? "Your payment isn't confirmed yet." : "Your payment didn't go through."}</b>{" "}
            {payError.msg}
          </Banner>
        </div>
      )}

      <div className="flex justify-between gap-3 mt-6">
        <Button variant="ghost" onClick={onBack} disabled={isSubmitting}>
          ← Back
        </Button>
        <Button disabled={isSubmitting || !stripe} onClick={handleSubmit}>
          {status === "finalizing"
            ? "Finalizing your payment…"
            : status === "filing"
            ? "Filing your return…"
            : isSubmitting
            ? "Processing…"
            : `Pay ${money(amount / 100)} & submit →`}
        </Button>
      </div>
    </Panel>
  );
}

const PaymentDetailsStep = ({ onBack }) => {
  const { state, dispatch, showToast } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  // The wizard also supports an unauthenticated dashboard-demo path (no
  // token / no saved return yet) - there's no return to charge a service fee
  // against, so that path skips Stripe entirely and just continues.
  const demoMode = !getToken() || !state.returnId;

  const [stripePromise, setStripePromise] = useState(null);
  // Placeholder cents until /config loads; the authoritative value is the
  // server's stripe.service-amount.
  const [amount, setAmount] = useState(2499);
  // Placeholder dollar-string breakdown until /config loads (sums to the
  // 2499-cent placeholder above); the authoritative split comes from
  // getServiceFeeConfig's serviceFeeAmount/processingFeeAmount.
  const [serviceFeeAmount, setServiceFeeAmount] = useState("23.99");
  const [processingFeeAmount, setProcessingFeeAmount] = useState("1.00");
  // Referral credit coming off the service fee (getServiceFeeConfig) - "0.00" when none.
  const [referralCreditAmount, setReferralCreditAmount] = useState("0.00");
  const [configError, setConfigError] = useState("");
  const [checkingPaid, setCheckingPaid] = useState(!demoMode);
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  // true when alreadyPaid came from a USED subscription-usage row rather than
  // a SUCCESS service-fee payment - only changes the banner copy below.
  const [alreadyPaidViaCredit, setAlreadyPaidViaCredit] = useState(false);
  const [noFeeDue, setNoFeeDue] = useState(false);
  const [filing, setFiling] = useState(false);

  // A rejected return that's already paid (Stripe fee or subscription credit)
  // may still owe MORE if the filer added vehicles beyond what was originally
  // filed/paid for (service_fee_rate bands the fee by total vehicle count, so
  // this is a band-delta, not a fresh full-count fee - see
  // ServiceFeeTopUpServiceImpl). Only checked/relevant once alreadyPaid is
  // true - loadTopUpConfigIfNeeded (below) is only ever called from inside
  // that branch of the paid-status effect.
  const [topUpRequired, setTopUpRequired] = useState(false);
  const [topUpStripePromise, setTopUpStripePromise] = useState(null);
  const [topUpAmount, setTopUpAmount] = useState(0);
  const [topUpServiceFeeAmount, setTopUpServiceFeeAmount] = useState("0.00");
  const [topUpProcessingFeeAmount, setTopUpProcessingFeeAmount] = useState("0.00");
  const [topUpExtraVehicleCount, setTopUpExtraVehicleCount] = useState(0);
  // Same "pay per return or use a credit" choice as the original fee below, scoped to the
  // top-up amount instead - a filer may have a subscription with returns remaining and
  // would rather spend a credit than pay by card for the extra vehicles.
  const [topUpEligibility, setTopUpEligibility] = useState(null); // null while loading, then { eligible, options: [...] }
  const [topUpPayMethod, setTopUpPayMethod] = useState(null); // null (not chosen yet) | 'fee' | 'subscription'
  const [reservingTopUpSubscription, setReservingTopUpSubscription] = useState(false);

  // Optional, discounted alternative to the per-vehicle fee below - only offered
  // when the filer has an active subscription with returns remaining. Both ways
  // to pay coexist permanently; this never replaces the fee flow, it's a choice.
  // A filer can hold more than one usable subscription at once (e.g. a SOLO and
  // a DUO-2 bought separately, each covering a different vehicle-count band), so
  // `options` may have more than one entry - the filer picks which to use.
  const [eligibility, setEligibility] = useState(null); // null while loading, then { eligible, options: [{ subscriptionId, categoryCode, categoryName, returnsRemaining, minVehicles, maxVehicles }] }
  const [payMethod, setPayMethod] = useState(null); // null (not chosen yet) | 'fee' | 'subscription'
  const [reservingSubscription, setReservingSubscription] = useState(false);

  // "You don't have a subscription yet" upsell - only for a filer with zero purchase
  // history (server-computed, see getUpsellStatus), and only while they haven't already
  // closed it once (permanent per-account dismissal, not just this page load).
  const [showUpsell, setShowUpsell] = useState(false);
  useEffect(() => {
    if (demoMode) return;
    let active = true;
    getSubscriptionUpsell()
      .then((r) => {
        if (active) setShowUpsell(!!r?.show);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode]);

  const dismissUpsell = () => {
    setShowUpsell(false); // optimistic - don't make the filer wait on the network to close it
    dismissSubscriptionUpsell().catch(() => {});
  };

  // Defensive path for a return whose resolved fee is genuinely $0 (no
  // filing type is priced that way today - every service_fee_rate band is
  // above $0 - but the config/intent endpoints still report it correctly if
  // one ever were). There's nothing for Stripe to charge (it rejects
  // PaymentIntents below its 50-cent minimum), so the backend marks the fee
  // row SUCCESS itself as soon as this is called, without ever creating a
  // PaymentIntent. Skip Stripe confirmation entirely and go straight to filing.
  const fileWithNoFee = async () => {
    setFiling(true);
    try {
      const created = await createServiceFeeIntent(state.returnId, { firstName: "", lastName: "", zip: "" });
      // source: null explicitly clears any stale source left in state.serviceFee - see
      // FeeForm's handleSubmit dispatch above for why omitting it is unsafe.
      dispatch({ type: "SET_SERVICE_FEE", patch: { status: "SUCCESS", paymentId: created.paymentId, source: null } });
    } catch (err) {
      showToast(err.message || "We couldn't file the return. Please review it and try again.");
      setFiling(false);
      return;
    }
    await fileReturn();
  };

  // Payment details is the wizard's last step - there's no further page to
  // advance to, so finishing here actually files the return: POST
  // /returns/{returnId}/submit (efileService.js) regenerates + validates the
  // IRS XML server-side and marks the return submitted, then we dispatch
  // FILE with that result and navigate to /confirm. Previously this just
  // dispatched FILE locally and navigated, so the XML was never generated.
  const fileReturn = async () => {
    if (demoMode) {
      dispatch({ type: "FILE" });
      navigate("/confirm");
      return;
    }
    setFiling(true);
    try {
      const result = await submitReturn(state.returnId);
      dispatch({ type: "FILE", result });
      navigate("/confirm");
    } catch (err) {
      // e.g. 422 (XML failed IRS schema validation) or 409 (service fee unpaid / return not ready).
      showToast(err.message || "We couldn't file the return. Please review it and try again.");
    } finally {
      setFiling(false);
    }
  };

  // Only called once alreadyPaid is established true (from either payment
  // path) - checks whether vehicles added since then still owe an additional
  // fee. Non-fatal on failure: if this silently fails to load, the filer just
  // sees the plain "already paid" screen and submit() 409s with the real
  // reason if something actually is owed (the server-side gate is what
  // actually protects revenue - this is only the friendly up-front prompt).
  const loadTopUpConfigIfNeeded = async (active) => {
    try {
      const cfg = await getServiceFeeTopUpConfig(state.returnId);
      if (!active || !cfg?.required) return;
      setTopUpRequired(true);
      setTopUpAmount(cfg.amount ?? 0);
      setTopUpServiceFeeAmount(cfg.serviceFeeAmount ?? "0.00");
      setTopUpProcessingFeeAmount(cfg.processingFeeAmount ?? "0.00");
      setTopUpExtraVehicleCount(cfg.extraVehicleCount ?? 0);
      if (cfg.publishableKey) setTopUpStripePromise(loadStripe(cfg.publishableKey));
      try {
        const e = await getSubscriptionTopUpEligibility(state.returnId);
        if (active) setTopUpEligibility(e);
      } catch {
        if (active) setTopUpEligibility({ eligible: false, options: [] });
      }
    } catch {
      /* see comment above - non-fatal */
    }
  };

  useEffect(() => {
    if (demoMode) return;
    let active = true;
    (async () => {
      // 1. If this return is already paid — via the Stripe service fee OR a
      //    subscription credit (a return pays through exactly one of the two,
      //    never both, but a rejected-return resubmit must recognize either) —
      //    gate the screen and don't render the card/credit-picker form (the
      //    backend would 409 anyway). No payment yet returns 404 from both
      //    checks; treat that as "not paid" and show the form.
      try {
        const p = await getServiceFeeStatus(state.returnId);
        if (active && p?.status === "SUCCESS") {
          // Deliberately NOT dispatching SET_SERVICE_FEE here - this branch only means
          // "already paid at some point", not "a payment was just made on this visit".
          // state.serviceFee is never sessionStorage-persisted (see AppContext.jsx), so
          // Confirmation.jsx's receipt card only shows when a payment actually completed
          // in THIS session (FeeForm/handleUseSubscriptionCredit/etc already dispatch it
          // themselves) - a plain resubmit that hits this "already paid" gate and goes
          // straight to Submit shouldn't resurrect an old (and possibly stale/wrong)
          // receipt. User decision 2026-08-20.
          setAlreadyPaid(true);
          setCheckingPaid(false);
          await loadTopUpConfigIfNeeded(active);
          return;
        }
      } catch {
        /* no service-fee payment yet → fall through and check the credit path */
      }
      try {
        const u = await getSubscriptionUsageStatus(state.returnId);
        if (active && u?.status === "USED") {
          // Same reasoning as the service-fee branch above - no SET_SERVICE_FEE dispatch.
          setAlreadyPaidViaCredit(true);
          setAlreadyPaid(true);
          setCheckingPaid(false);
          await loadTopUpConfigIfNeeded(active);
          return;
        }
      } catch {
        /* no subscription usage row either → fall through and load the form */
      }
      if (!active) return;
      setCheckingPaid(false);

      // 2. Not paid — load Stripe config and render the payment form.
      try {
        const cfg = await getServiceFeeConfig(state.returnId);
        if (!active) return;
        if (!cfg?.publishableKey) {
          setConfigError("Payments are temporarily unavailable. Please try again later.");
          return;
        }
        // `cfg.amount` is 0 (falsy) for a genuinely free filing (e.g. VIN
        // correction, priced at $0.00 in service_fee_rate) - a truthiness
        // check here would silently keep the $24.99 placeholder default
        // instead of showing/charging $0, so check for presence, not truthiness.
        if (cfg.amount != null) setAmount(cfg.amount);
        if (cfg.serviceFeeAmount != null) setServiceFeeAmount(cfg.serviceFeeAmount);
        if (cfg.processingFeeAmount != null) setProcessingFeeAmount(cfg.processingFeeAmount);
        if (cfg.referralCreditAmount != null) setReferralCreditAmount(cfg.referralCreditAmount);
        if (!cfg.feeRequired) {
          // Nothing for Stripe to charge (it rejects PaymentIntents below its
          // 50-cent minimum) - skip loading Stripe/the Payment Element.
          setNoFeeDue(true);
          return;
        }
        setStripePromise(loadStripe(cfg.publishableKey));
      } catch {
        if (active) setConfigError("Payments are temporarily unavailable. Please try again later.");
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode, state.returnId]);

  // Display-only check: whether to even offer "use a subscription credit" as an
  // option, and which of the filer's credits actually match THIS return's filing
  // type (an Amendments credit for an amended return, every other credit for
  // original/final - never mixed, see the backend's coversFormType). The backend
  // enforces the real gate independently when the return is actually submitted,
  // so a stale/wrong read here can't let anyone file for free.
  useEffect(() => {
    if (demoMode) return;
    let active = true;
    (async () => {
      try {
        const e = await getSubscriptionEligibility(state.returnId);
        if (active) setEligibility(e);
      } catch {
        if (active) setEligibility({ eligible: false, options: [] });
      }
    })();
    return () => {
      active = false;
    };
  }, [demoMode, state.returnId]);

  // Reserves a credit from the filer's subscription, then files the return the
  // same way the per-vehicle fee path does. Reserving does NOT itself pay for
  // the return - the credit is only permanently spent once submitReturn (inside
  // fileReturn) actually clears the backend's generation/submission gate.
  const handleUseSubscriptionCredit = async (subscriptionId) => {
    if (!subscriptionId) return;
    setReservingSubscription(true);
    try {
      await reserveSubscriptionCredit(state.returnId, subscriptionId);
    } catch (err) {
      // A 409 with THIS specific message means this return already has a live
      // reservation (e.g. a retried submit after a prior attempt failed) -
      // that's fine, proceed straight to filing. Any other 409 - no returns
      // remaining, or (most importantly) the return's vehicle count doesn't
      // fit this plan's fleet-size band (e.g. a SOLO credit only covers a
      // 1-vehicle return) - is a real block: show it and stop, don't file.
      const alreadyReserved = err.status === 409 && /already has a reserved subscription credit/i.test(err.message || "");
      if (!alreadyReserved) {
        showToast(err.message || "We couldn't reserve a subscription credit. You can pay per return instead.");
        setReservingSubscription(false);
        return;
      }
    }
    // So the confirmation screen (reached via fileReturn's navigate, below) can show
    // the receipt card/download button the same way it does for a Stripe-paid return -
    // there's no ServiceFeePayment row for a credit-paid one, so it needs this usage
    // row's own id instead (source=SUBSCRIPTION_CREDIT tells it which table `paymentId`
    // means, per getServiceFeeReceipt's contract).
    try {
      const u = await getSubscriptionUsageStatus(state.returnId);
      if (u?.id) {
        dispatch({ type: "SET_SERVICE_FEE", patch: { status: "SUCCESS", paymentId: u.id, source: "SUBSCRIPTION_CREDIT" } });
      }
    } catch {
      /* non-fatal - confirmation screen just won't show the receipt card */
    }
    setReservingSubscription(false);
    await fileReturn();
  };

  // Top-up counterpart of handleUseSubscriptionCredit above: spends a return
  // credit on the ADDITIONAL fee for vehicles added beyond what this return
  // was originally paid for, instead of paying it by card. Dispatches
  // SET_SERVICE_FEE with THIS top-up usage row's own id (source=
  // SUBSCRIPTION_TOPUP_CREDIT) so the confirmation screen's receipt shows
  // this top-up, not the return's original (now-stale) fee/credit payment -
  // same reasoning as the card top-up path's serviceFeeSource, and the same
  // "fetch the row's id right after reserving" pattern handleUseSubscriptionCredit
  // uses above.
  const handleUseTopUpSubscriptionCredit = async (subscriptionId) => {
    if (!subscriptionId) return;
    setReservingTopUpSubscription(true);
    try {
      await reserveSubscriptionTopUpCredit(state.returnId, subscriptionId);
    } catch (err) {
      const alreadyReserved = err.status === 409
        && /already has a reserved subscription credit/i.test(err.message || "");
      if (!alreadyReserved) {
        showToast(err.message || "We couldn't reserve a subscription credit. You can pay by card instead.");
        setReservingTopUpSubscription(false);
        return;
      }
    }
    try {
      const u = await getSubscriptionTopUpUsageStatus(state.returnId);
      if (u?.id) {
        dispatch({
          type: "SET_SERVICE_FEE",
          patch: { status: "SUCCESS", paymentId: u.id, source: "SUBSCRIPTION_TOPUP_CREDIT" },
        });
      }
    } catch {
      /* non-fatal - confirmation screen just won't show the receipt card */
    }
    setReservingTopUpSubscription(false);
    await fileReturn();
  };

  if (demoMode) {
    return (
      <Panel padded>
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
          Payment details
        </div>
        <h2 className="text-2xl m-[4px_0]">Pay the e-filing service fee</h2>
        <p className="text-[var(--muted)] max-w-[62ch] mb-4">
          Sign in and save a return to pay the e-filing service fee here.
        </p>
        <div className="flex justify-between gap-3 mt-6">
          <Button variant="ghost" onClick={onBack} disabled={filing}>
            ← Back
          </Button>
          <Button onClick={fileReturn} disabled={filing}>
            {filing ? "Filing your return…" : "Submit to the IRS →"}
          </Button>
        </div>
      </Panel>
    );
  }

  if (checkingPaid) {
    return (
      <Panel padded>
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
          Payment details
        </div>
        <p className="text-[var(--muted)] mt-3">Checking payment status…</p>
      </Panel>
    );
  }

  if (alreadyPaid) {
    // Already paid the ORIGINAL fee/credit, but vehicles added since then push
    // this rejected-return resubmission past what that covered - an additional
    // fee (the service_fee_rate band delta, not a fresh full-count fee) must be
    // paid before Submit will succeed (Return2290XmlServiceImpl enforces this
    // for real; this is just the friendly up-front prompt for it).
    if (topUpRequired) {
      // Same "pay per return or use a credit" choice as the original fee (below) -
      // only shown when there's actually a credit that can cover this top-up.
      if (topUpEligibility?.eligible && topUpPayMethod === null) {
        const busy = reservingTopUpSubscription || filing;
        return (
          <Panel padded>
            <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
              Payment details
            </div>
            <h2 className="text-2xl m-[4px_0]">How would you like to pay the additional fee?</h2>
            <p className="text-[var(--muted)] max-w-[62ch] mb-4">
              You added {topUpExtraVehicleCount} vehicle{topUpExtraVehicleCount === 1 ? "" : "s"} beyond what was
              originally filed and paid for on this return. Use a return credit to cover it, or pay by card.
            </p>

            <div className="grid gap-3.5 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setTopUpPayMethod("fee")}
                disabled={busy}
                className="text-left border-[1.5px] border-[var(--line-2)] rounded-[var(--r-sm)] p-4 bg-[var(--panel)] hover:border-[var(--accent)] transition-colors disabled:opacity-60"
              >
                <div className="text-[14px] font-bold text-[var(--ink)] mb-1">Pay by card</div>
                <div className="text-[13px] text-[var(--muted)]">
                  Pay the additional service fee ({money(topUpAmount / 100)}) for this return.
                </div>
              </button>

              {topUpEligibility.options.map((opt) => (
                <button
                  key={opt.subscriptionId}
                  type="button"
                  onClick={() => handleUseTopUpSubscriptionCredit(opt.subscriptionId)}
                  disabled={busy}
                  className="text-left border-[1.5px] border-[var(--accent)] rounded-[var(--r-sm)] p-4 bg-[var(--accent-soft)] hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  <div className="text-[14px] font-bold text-[var(--ink)] mb-1">Use {opt.categoryName} credit</div>
                  <div className="text-[13px] text-[var(--muted)]">
                    {opt.returnsRemaining} return{opt.returnsRemaining === 1 ? "" : "s"} remaining — no card charge
                    for this additional fee.
                  </div>
                  <div className="text-[12px] font-semibold text-[var(--ok)] mt-1.5">✓ Active</div>
                </button>
              ))}
            </div>

            <div className="flex justify-between gap-3 mt-6">
              <Button variant="ghost" onClick={onBack} disabled={busy}>
                ← Back
              </Button>
              {busy && (
                <span className="text-[13px] text-[var(--muted)] self-center">
                  {reservingTopUpSubscription ? "Reserving your credit…" : "Filing your return…"}
                </span>
              )}
            </div>
          </Panel>
        );
      }
      // Amended returns may only ever pay the top-up with an Amendments-plan credit (never
      // a fleet-size plan like SOLO/DUO2/STARTER24 - 2026-08-18 user decision: pricing basis
      // and which credit family may pay for it are kept separate, see
      // SubscriptionUsageServiceImpl.checkTopUpEligibility). If the filer has none (never
      // bought one, or already used it up), don't silently fall to card with no explanation -
      // point them at buying the plan, same as an eligible filer is pointed at using one.
      const noAmendmentsCreditNote =
        state.returnType === "amended" && topUpEligibility && !topUpEligibility.eligible ? (
          <Banner tone="warn" icon="⚠" className="mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3 w-full">
              <span>
                You've already used your Amendments plan credit for this account. To pay this
                additional fee with a subscription instead of a card, get the Amendments plan
                and come back to continue.
              </span>
              <Button
                className="!h-9 !px-4 !text-[13px]"
                onClick={() => navigate("/dashboard/subscriptions", { state: { returnTo: location.pathname } })}
              >
                Get the Amendments plan
              </Button>
            </div>
          </Banner>
        ) : null;
      if (!topUpStripePromise) {
        return (
          <>
            {noAmendmentsCreditNote}
            <Panel padded>
              <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
                Payment details
              </div>
              <p className="text-[var(--muted)] mt-3">Loading secure payment form…</p>
            </Panel>
          </>
        );
      }
      const topUpElementsOptions = {
        mode: "payment",
        amount: topUpAmount,
        currency: "usd",
        appearance: { theme: "stripe" },
      };
      return (
        <>
          {noAmendmentsCreditNote}
          <Elements stripe={topUpStripePromise} options={topUpElementsOptions}>
            <FeeForm
              onBack={onBack}
              returnId={state.returnId}
              amount={topUpAmount}
              serviceFeeAmount={topUpServiceFeeAmount}
              processingFeeAmount={topUpProcessingFeeAmount}
              showToast={showToast}
              dispatch={dispatch}
              fileReturn={fileReturn}
              createIntentFn={createServiceFeeTopUpIntent}
              statusFn={getServiceFeeTopUpStatus}
              serviceFeeSource="SERVICE_FEE_TOPUP"
              heading="Pay the additional service fee"
              description={`You added ${topUpExtraVehicleCount} vehicle${
                topUpExtraVehicleCount === 1 ? "" : "s"
              } beyond what was originally filed and paid for on this return. Pay the additional service fee below, then submit to the IRS.`}
            />
          </Elements>
        </>
      );
    }
    return (
      <Panel padded>
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
          Payment details
        </div>
        <h2 className="text-2xl m-[4px_0]">Pay the e-filing service fee</h2>
        <Banner tone="info" icon="✓" className="my-4">
          {alreadyPaidViaCredit
            ? "This return was already paid for with a subscription credit. No additional payment is required for this resubmission — you can submit your corrected return to the IRS."
            : "The service fee for this return has already been paid. You can submit your return to the IRS."}
        </Banner>
        <div className="flex justify-between gap-3 mt-6">
          <Button variant="ghost" onClick={onBack} disabled={filing}>
            ← Back
          </Button>
          <Button onClick={fileReturn} disabled={filing}>
            {filing ? "Filing your return…" : "Submit to the IRS →"}
          </Button>
        </div>
      </Panel>
    );
  }

  if (noFeeDue) {
    return (
      <Panel padded>
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
          Payment details
        </div>
        <h2 className="text-2xl m-[4px_0]">Pay the e-filing service fee</h2>
        <Banner tone="info" icon="✓" className="my-4">
          {Number(referralCreditAmount) > 0
            ? `Your referral credit covers the ${money(serviceFeeAmount)} service fee for this filing. You can submit your return to the IRS.`
            : "There's no service fee for this filing. You can submit your return to the IRS."}
        </Banner>
        <div className="flex justify-between gap-3 mt-6">
          <Button variant="ghost" onClick={onBack} disabled={filing}>
            ← Back
          </Button>
          <Button onClick={fileWithNoFee} disabled={filing}>
            {filing ? "Filing your return…" : "Submit to the IRS →"}
          </Button>
        </div>
      </Panel>
    );
  }

  // Let the filer choose how to pay for this specific return: the per-vehicle
  // service fee (below), or a subscription credit if they have one available.
  // Only shown when there's actually a credit to offer - otherwise this step
  // behaves exactly as it always has.
  if (eligibility?.eligible && payMethod === null) {
    const busy = reservingSubscription || filing;
    return (
      <Panel padded>
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
          Payment details
        </div>
        <h2 className="text-2xl m-[4px_0]">How would you like to pay?</h2>
        <p className="text-[var(--muted)] max-w-[62ch] mb-4">
          You have return credits available from a subscription package. Use one to skip the per-vehicle service fee
          on this return, or pay per return as usual.
        </p>

        <div className="grid gap-3.5 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setPayMethod("fee")}
            disabled={busy}
            className="text-left border-[1.5px] border-[var(--line-2)] rounded-[var(--r-sm)] p-4 bg-[var(--panel)] hover:border-[var(--accent)] transition-colors disabled:opacity-60"
          >
            <div className="text-[14px] font-bold text-[var(--ink)] mb-1">Pay per return</div>
            <div className="text-[13px] text-[var(--muted)]">
              Pay the e-filing service fee for this return, same as always.
            </div>
          </button>

          {/* One card per usable subscription - a filer who's bought more than one
              package (e.g. a SOLO and a DUO-2) sees and picks between all of them,
              instead of only ever being offered the oldest one. */}
          {eligibility.options.map((opt) => (
            <button
              key={opt.subscriptionId}
              type="button"
              onClick={() => handleUseSubscriptionCredit(opt.subscriptionId)}
              disabled={busy}
              className="text-left border-[1.5px] border-[var(--accent)] rounded-[var(--r-sm)] p-4 bg-[var(--accent-soft)] hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              <div className="text-[14px] font-bold text-[var(--ink)] mb-1">Use {opt.categoryName} credit</div>
              <div className="text-[13px] text-[var(--muted)]">
                {opt.returnsRemaining} return{opt.returnsRemaining === 1 ? "" : "s"} remaining — no additional
                charge for this return.
              </div>
              <div className="text-[12px] font-semibold text-[var(--ok)] mt-1.5">✓ Active</div>
            </button>
          ))}
        </div>

        <div className="flex justify-between gap-3 mt-6">
          <Button variant="ghost" onClick={onBack} disabled={busy}>
            ← Back
          </Button>
          {busy && (
            <span className="text-[13px] text-[var(--muted)] self-center">
              {reservingSubscription ? "Reserving your credit…" : "Filing your return…"}
            </span>
          )}
        </div>
      </Panel>
    );
  }

  if (configError) {
    // TEMPORARY: Stripe isn't configured (blank stripe.publishable-key) - show
    // the preview layout instead of hard-blocking the page. Remove this
    // branch (and FeeFormPreview above) once real Stripe test keys are set.
    return (
      <FeeFormPreview
        onBack={onBack}
        amount={amount}
        serviceFeeAmount={serviceFeeAmount}
        referralCreditAmount={referralCreditAmount}
        processingFeeAmount={processingFeeAmount}
      />
    );
  }

  if (!stripePromise) {
    return (
      <Panel padded>
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
          Payment details
        </div>
        <p className="text-[var(--muted)] mt-3">Loading secure payment form…</p>
      </Panel>
    );
  }

  const elementsOptions = {
    mode: "payment",
    amount,
    currency: "usd",
    appearance: { theme: "stripe" },
  };

  return (
    <>
      {/* Only ever shown to a filer with zero subscription purchase history (server-computed,
          getSubscriptionUpsell) - someone who already has/had a subscription never sees this,
          regardless of whether it covers this particular return. Closing it is remembered for
          the account permanently (dismissSubscriptionUpsell), not just this page load. */}
      {showUpsell && (
        <Banner tone="info" icon="💳" className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3 w-full">
            <span>We have a subscription plan for your filing — save on this and future returns.</span>
            <div className="flex items-center gap-2 flex-none">
              <Button
                className="!h-9 !px-4 !text-[13px]"
                onClick={() => navigate("/dashboard/subscriptions", { state: { returnTo: location.pathname } })}
              >
                View plans
              </Button>
              <button
                type="button"
                onClick={dismissUpsell}
                aria-label="Dismiss"
                className="text-[var(--muted)] hover:text-[var(--ink)] bg-transparent border-0 cursor-pointer text-lg leading-none px-1"
              >
                ×
              </button>
            </div>
          </div>
        </Banner>
      )}
      <Elements stripe={stripePromise} options={elementsOptions}>
        <FeeForm
          onBack={onBack}
          returnId={state.returnId}
          amount={amount}
          serviceFeeAmount={serviceFeeAmount}
          referralCreditAmount={referralCreditAmount}
          processingFeeAmount={processingFeeAmount}
          showToast={showToast}
          dispatch={dispatch}
          fileReturn={fileReturn}
        />
      </Elements>
    </>
  );
};

export default PaymentDetailsStep;
